import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  Divider,
} from '@makeably/creativex-design-system';
import ItemDetails from 'components/internal/review/ItemDetails';
import { getScoreType } from 'components/internal/scores/shared';
import {
  emptyState,
  getDateTimeDimension,
  NULL_VALUE,
} from 'components/internal/shared';
import ItemsTable from 'components/molecules/ItemsTable';
import TierForm from 'components/rules/tiers/TierForm';
import { tierProps } from 'components/rules/tiers/TierRow';
import {
  internalGuidelinePath,
  internalScoreSubscriptionsPath,
} from 'utilities/routes';
import { pluralizeByCount } from 'utilities/string';
import styles from './Score.module.css';

const propTypes = {
  guidelines: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  score: PropTypes.shape({
    description: PropTypes.string,
    id: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    qualityRateName: PropTypes.string.isRequired,
    qualitySpendRateName: PropTypes.string.isRequired,
    scoreType: PropTypes.string.isRequired,
    subscriptionCount: PropTypes.number.isRequired,
    tiers: PropTypes.arrayOf(
      PropTypes.shape(tierProps),
    ).isRequired,
    updatedAt: PropTypes.string.isRequired,
  }).isRequired,
};

const details = [
  {
    key: 'label',
    label: 'Label',
  },
  {
    key: 'scoreType',
    label: 'Score Type',
  },
  {
    key: 'updatedAt',
    label: 'Last Updated',
  },
  {
    key: 'name',
    label: 'Custom Score Name',
  },
  {
    key: 'qualityRateName',
    label: 'Custom Quality Rate',
  },
  {
    key: 'qualitySpendRateName',
    label: 'Custom Quality Spend Rate',
  },
];

const headers = [
  {
    key: 'name',
    label: 'Guideline',
    sortable: false,
  },
  {
    key: 'description',
    label: 'Description',
    sortable: false,
  },
];

function getItems(guidelines) {
  return guidelines.map(({
    id,
    name,
    description,
  }) => ({
    description: { value: description || NULL_VALUE },
    id: { value: id },
    name: {
      element: <a href={internalGuidelinePath(id)}>{ name }</a>,
      value: name,
    },
  }));
}

function Score({
  guidelines,
  score: {
    description,
    id,
    label,
    name,
    qualityRateName,
    qualitySpendRateName,
    scoreType,
    subscriptionCount,
    tiers,
    updatedAt,
  },
}) {
  const scoreItem = {
    label: { value: label },
    name: { value: name },
    qualityRateName: { value: qualityRateName },
    qualitySpendRateName: { value: qualitySpendRateName },
    scoreType: { value: getScoreType(scoreType) },
    updatedAt: getDateTimeDimension(updatedAt),
  };
  const guidelineItems = useMemo(() => getItems(guidelines), [guidelines]);
  const formattedTiers = useMemo(() => tiers.map((
    tier,
    idx,
  ) => {
    const { rank } = tier;

    const isHighest = rank === 'highest';
    const maximum = isHighest ? 100 : tiers[idx - 1].minimumCutoff;

    return {
      ...tier,
      maximum,
    };
  }), [tiers]);

  return (
    <Card className="u-flexColumn u-gap-24">
      <div className="u-flexColumn">
        <div className="u-flexRow u-justifyEnd">
          <Button
            iconRight="arrowRight"
            label={`View ${subscriptionCount} ${pluralizeByCount('Subscription', subscriptionCount)}`}
            url={internalScoreSubscriptionsPath(id)}
            variant="tertiary"
          />
        </div>
        <h4>{ `Template Score: ${name}` }</h4>
        <div className="t-body-1 u-marginTop-8">{ description }</div>
      </div>
      <ItemDetails
        details={details}
        item={scoreItem}
      />
      <Divider />
      <div className={styles.table}>
        <h5>Guidelines</h5>
        <ItemsTable
          className="u-scrollShadowRight"
          emptyTableContent={emptyState}
          headers={headers}
          items={guidelineItems}
          maxWidthSize="custom"
        />
      </div>
      <div className="u-flexColumn u-gap-16">
        <h5>Tiers</h5>
        <TierForm tiers={formattedTiers} />
      </div>
    </Card>
  );
}

Score.propTypes = propTypes;

export default Score;
